'use client';
import * as Sentry from '@sentry/react';
import Cookies from 'js-cookie';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import useSWRImmutable from 'swr/immutable';
import { COOKIE_NAME } from '@/helpers/cookie';
import { fetcherSS } from '@/modules/api';
import { sendEmptyCookieStagEvent } from '@/modules/googleAnalytics';
import {
  STAG_PARAM,
  TRACKING_USER_PARAMS_COOKIE,
  USER_TRACKING_PARAMS_SS,
} from '../constants';
import {
  getCleanUrl,
  getMarketingParams,
  getParamsToStore,
  getTrackingUserParamsCookie,
} from '../helpers';
import { useTrackingUserParamsStore } from '../store';
import type { TrackingParams } from '../types';

/**
 * Hook to manage user tracking parameters in the URL for SoftSwiss.
 */
export const useTrackingUserParamsHook = () => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [queryString, setQueryString] = useState<string | null>(null);
  const setParams = useTrackingUserParamsStore(
    (state) => state.setTrackingUserParams
  );

  useSWRImmutable(
    queryString ? `/info/project?${queryString}` : null,
    fetcherSS,
    {
      onError: (error) => {
        Sentry.captureException(
          error || new Error('Sending tracking params to SS error')
        );
      },
      onSuccess: () => {
        const sentStag = new URLSearchParams(queryString || '').has(STAG_PARAM);

        if (sentStag && !Cookies.get(COOKIE_NAME.STAG)) {
          sendEmptyCookieStagEvent(queryString || '');
        }
      },
    }
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.size === 0) return;

    const paramsToStore: TrackingParams = getParamsToStore(
      params,
      USER_TRACKING_PARAMS_SS
    );

    if (Object.keys(paramsToStore).length === 0) return;

    const isActiveSession = Cookies.get(COOKIE_NAME.ACTIVE_SESSION) === 'true';
    const isRegistered = Cookies.get(COOKIE_NAME.IS_REGISTERED) === 'true';
    const isAuth = isActiveSession || isRegistered;

    const allowedParams = isAuth
      ? getMarketingParams(paramsToStore)
      : paramsToStore;

    if (Object.keys(allowedParams).length > 0) {
      Cookies.set(
        TRACKING_USER_PARAMS_COOKIE,
        JSON.stringify({ ...getTrackingUserParamsCookie(), ...allowedParams })
      );
      setParams(allowedParams);

      if (!isActiveSession) {
        setQueryString(
          new URLSearchParams(
            allowedParams as Record<string, string>
          ).toString()
        );
      }
    }

    router.replace(getCleanUrl(params), { scroll: false });
  }, [searchParams, router, setParams]);
};
